import { getControllerApiVersion, isZonable, IsExcludedBasedOnTypeOrProject } from './utils';
import { permissionCategory } from './access_rights';
import { supportsNutritionTanks } from './nutrition_utils';

const present = (settings, category) => settings?.[category] != null;
const canRead = (controller, category) => {
  const permission = permissionCategory(category);
  return controller?.access_rights?.[permission]?.read ?? permission !== 'multi_io';
};
const supportedTab = (controller, category) =>
  !IsExcludedBasedOnTypeOrProject(controller, category, 'tabs');

// Readiness follows the forms this controller/user can open, not the number of
// keys in telemetry. API gates match the existing DTO categories; model/project
// exclusions also omit settings that the controller never supplies.
export function hasRequiredSettings(settings, controller) {
  if (settings == null) return false;
  const version = getControllerApiVersion(controller);
  const zoned = isZonable(controller);
  const industrialPro = controller.type === 6000;
  const categories = ['climate', 'co2', 'ventilation', 'timing', 'lights', 'advanced'];
  if (!zoned) categories.push('system', 'multi_out');
  if (!zoned || industrialPro || version < 'BAI') categories.push('alarms');
  if (industrialPro || version >= 'AEB') categories.push('nutrition', 'irrigation');
  if (
    !categories.every(
      (category) =>
        !canRead(controller, category) ||
        !supportedTab(controller, category) ||
        present(settings, category)
    )
  )
    return false;
  // Scheduling is sent after ordinary settings and has its own loading state.
  // Its split recipe must not prevent the other controller pages from opening.
  return true;
}

function hasGlobalSettings(data, controller) {
  // System supplies shared units/version metadata even when settings rights are
  // denied. Requiring it also distinguishes all-denied readback from live values.
  if (!present(data, 'system')) return false;
  const needsIO = canRead(controller, 'multi_io') && supportedTab(controller, 'multi_in_out');
  // Industrial Pro's Python producer sends system/slaves globally and its
  // settings per zone. Its API sequence is independent of the ESP's API gates.
  if (controller.type === 6000) {
    return !needsIO || hasSlaveAssignments(data);
  }
  const version = getControllerApiVersion(controller);
  const requires = (category, minimum, permission = category) =>
    version < minimum || !canRead(controller, permission) || present(data, category);
  if (
    !requires('alarms', 'BAI') ||
    !requires('ext_readings', 'AED') ||
    !requires('ahu_info', 'BAA') ||
    !requires('ahu_unit', 'BAA') ||
    !requires('hvac', 'BAE')
  )
    return false;
  if (
    supportsNutritionTanks(controller) &&
    supportedTab(controller, 'nutrition') &&
    canRead(controller, 'nutrition') &&
    !present(data, 'nutrition')
  )
    return false;
  const needsTankInfo =
    canRead(controller, 'irrigation_tank_info') &&
    (controller.access_rights?.irrigation_tank_info?.read === true ||
      ['nutrition', 'irrigation'].some(
        (category) => canRead(controller, category) && supportedTab(controller, category)
      ));
  if (supportsNutritionTanks(controller) && needsTankInfo && !present(data, 'irrigation_tank_info')) return false;
  if (needsIO) {
    if (
      !requires('slaves', 'ADEE', 'multi_io') ||
      !requires('multi_io_counters', 'ADF', 'multi_io') ||
      !requires('overwr_io', 'AE') ||
      !requires('user_func_sets', 'AEC')
    )
      return false;
    // State-only slave rows do not contain the assignments needed by IO forms.
    if (version >= 'ADEE' && !hasSlaveAssignments(data)) return false;
  }
  return true;
}

function hasSlaveAssignments(data) {
  return (
    Array.isArray(data.slaves) &&
    data.slaves.every(
      (slave) =>
        Array.isArray(slave.multi_outs) &&
        slave.multi_outs.every((io) => present(io, 'func') && present(io, 'id'))
    )
  );
}

export function hasFullSettingsPayload(data, controller) {
  if (data == null || controller == null) return false;
  const current = data.system
    ? { ...controller, settings: { ...controller.settings, system: data.system } }
    : controller;
  if (isZonable(current)) {
    return (
      hasGlobalSettings(data, current) &&
      Array.isArray(data.zones) &&
      data.zones.every(
        (zone) =>
          present(zone.settings, 'cur_values') && hasRequiredSettings(zone.settings, current)
      )
    );
  }
  return (
    present(data, 'system') && present(data, 'cur_values') && hasRequiredSettings(data, current)
  );
}
